import { ReactNode, useMemo, isValidElement } from 'react';

import { Popover } from '../popover/popover';

export interface DescriptionItemProps {
  title: string | ReactNode;
  value: string | number | ReactNode;
  additionalInfo?: string;
}

const DescriptionListItem = ({ title, value, additionalInfo }: DescriptionItemProps) => {
  const content = useMemo(() => {
    if (isValidElement(value)) {
      return value;
    } else {
      return additionalInfo ? (
        <Popover content={additionalInfo} title={value}>
          <span className="cursor-pointer text-gray-900 underline decoration-dashed underline-offset-2">{value}</span>
        </Popover>
      ) : (
        <span className="text-gray-900">{value}</span>
      );
    }
  }, [value, additionalInfo]);

  return (
    <>
      <div className="border-b border-r border-gray-300 bg-gray-50 px-6 py-4 text-gray-800">{title}</div>
      <div className="border-b border-gray-300 bg-white px-6 py-4">{content}</div>
    </>
  );
};

export interface DescriptionListProps {
  /** The data to be displayed in the description list */
  data: DescriptionItemProps[];
}

export const DescriptionList = ({ data = [] }: DescriptionListProps) => {
  // Memoize description items
  const items = useMemo(
    () =>
      data.map((d, index) => (
        <DescriptionListItem title={d.title} value={d.value} key={index} additionalInfo={d.additionalInfo} />
      )),
    [data]
  );
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] overflow-hidden rounded-lg border border-gray-300">{items}</div>
  );
};

export default DescriptionList;
